import type { Trade } from '../types';
import { classifyPnl } from './classify.js';
import { utcHourToTz } from './timezone.js';

/**
 * The Coach — trading psychology mentor engine.
 *
 * Layer 1: leak diagnosis. Pure functions that scan the full trade history and
 * rank behavioral leaks (revenge trading, overtrading, size escalation...) by
 * dollar cost. Grounded in the trading psychology literature: judge process,
 * not outcome (Douglas); measure yourself on rule-breaks (Tharp).
 *
 * Layer 2: assignments. Behavioral drills measured against real trades —
 * compliance is computed automatically, so progress can't be faked.
 */

// ─── Leak Diagnosis ──────────────────────────────────────────

export type LeakId =
  | 'revenge_trading'
  | 'overtrading'
  | 'size_escalation'
  | 'no_stop_after_losses'
  | 'weak_hour'
  | 'low_discipline'
  | 'daily_loss_breach';

export interface Leak {
  id: LeakId;
  /** Total $ cost of this leak over the analyzed period (positive number). */
  costDollars: number;
  /** Number of trades / days involved. */
  occurrences: number;
  /** 0-1, how confident the diagnosis is (sample size driven). */
  confidence: number;
  title_he: string;
  title_en: string;
  detail_he: string;
  detail_en: string;
  /** Recommended assignment template for fixing this leak. */
  fixTemplateId: TemplateId;
  /** Override for the template param (e.g. the specific weak hour). */
  fixParam?: number;
}

function fmtUsd(n: number): string {
  return '$' + Math.abs(Math.round(n)).toLocaleString('en-US');
}

/** Minutes since midnight from 'HH:MM[:SS]', or null. */
function timeMins(t?: string): number | null {
  if (!t) return null;
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** Sort trades chronologically: date, then entry time (missing times last within day). */
export function sortChrono(trades: Trade[]): Trade[] {
  return [...trades].sort(
    (a, b) =>
      a.trade_date.localeCompare(b.trade_date) ||
      (a.entry_time ?? '99:99').localeCompare(b.entry_time ?? '99:99'),
  );
}

function groupByDay(trades: Trade[]): Map<string, Trade[]> {
  const days = new Map<string, Trade[]>();
  for (const t of sortChrono(trades)) {
    const arr = days.get(t.trade_date);
    if (arr) arr.push(t);
    else days.set(t.trade_date, [t]);
  }
  return days;
}

/**
 * Trades entered within `windowMins` of the previous trade's exit, when that
 * previous trade was a loss. Falls back to "next trade same day after a loss"
 * when times are missing.
 */
export function findRevengeTrades(trades: Trade[], beRange: number, windowMins = 30): Trade[] {
  const out: Trade[] = [];
  for (const day of groupByDay(trades).values()) {
    for (let i = 1; i < day.length; i++) {
      const prev = day[i - 1];
      if (classifyPnl(prev.pnl, beRange) !== 'loss') continue;
      const prevExit = timeMins(prev.exit_time) ?? timeMins(prev.entry_time);
      const entry = timeMins(day[i].entry_time);
      if (prevExit !== null && entry !== null) {
        const gap = entry - prevExit;
        if (gap >= 0 && gap <= windowMins) out.push(day[i]);
      } else {
        // No usable times — treat immediate next trade after a loss as suspect
        out.push(day[i]);
      }
    }
  }
  return out;
}

export function diagnoseLeaks(
  trades: Trade[],
  beRange: number,
  dailyMaxLoss: number = 0,
  /** IANA timezone for hours shown in detail text. Logic stays UTC (entry_time is stored UTC). */
  tz?: string,
): Leak[] {
  if (trades.length < 10) return [];
  const leaks: Leak[] = [];
  const days = groupByDay(trades);
  const isL = (t: Trade) => classifyPnl(t.pnl, beRange) === 'loss';
  const isW = (t: Trade) => classifyPnl(t.pnl, beRange) === 'win';

  // ── Revenge trading: trades shortly after a loss ──
  const revenge = findRevengeTrades(trades, beRange);
  const revengePnl = revenge.reduce((s, t) => s + t.pnl, 0);
  if (revenge.length >= 5 && revengePnl < 0) {
    leaks.push({
      id: 'revenge_trading',
      costDollars: -revengePnl,
      occurrences: revenge.length,
      confidence: Math.min(1, revenge.length / 15),
      title_he: 'מסחר נקמה',
      title_en: 'Revenge trading',
      detail_he: `${revenge.length} עסקאות נפתחו תוך 30 דקות אחרי הפסד — סה"כ ${fmtUsd(revengePnl)}-. העצמי הרגוע שלך לא היה לוקח אותן.`,
      detail_en: `${revenge.length} trades entered within 30 min of a loss — total ${fmtUsd(revengePnl)} lost. Your calm self wouldn't have taken them.`,
      fixTemplateId: 'cooldown_after_loss',
    });
  }

  // ── Overtrading: P&L of trades 4+ within each day ──
  let lateTrades = 0,
    latePnl = 0,
    earlyPnl = 0;
  for (const day of days.values()) {
    for (let i = 0; i < day.length; i++) {
      if (i < 3) earlyPnl += day[i].pnl;
      else {
        lateTrades++;
        latePnl += day[i].pnl;
      }
    }
  }
  if (lateTrades >= 5 && latePnl < 0) {
    leaks.push({
      id: 'overtrading',
      costDollars: -latePnl,
      occurrences: lateTrades,
      confidence: Math.min(1, lateTrades / 20),
      title_he: 'מסחר יתר',
      title_en: 'Overtrading',
      detail_he: `עסקאות מהרביעית והלאה ביום עלו לך ${fmtUsd(latePnl)}-. שלוש הראשונות: ${earlyPnl >= 0 ? '+' : '-'}${fmtUsd(earlyPnl)}.`,
      detail_en: `Trades #4 and beyond each day cost you ${fmtUsd(latePnl)}. Your first three: ${earlyPnl >= 0 ? '+' : '-'}${fmtUsd(earlyPnl)}.`,
      fixTemplateId: 'max_trades_per_day',
    });
  }

  // ── Size escalation: losses after a loss vs losses after a win ──
  const sorted = sortChrono(trades);
  const lossesAfterLoss: number[] = [];
  const lossesAfterWin: number[] = [];
  for (let i = 1; i < sorted.length; i++) {
    if (!isL(sorted[i])) continue;
    if (isL(sorted[i - 1])) lossesAfterLoss.push(Math.abs(sorted[i].pnl));
    else if (isW(sorted[i - 1])) lossesAfterWin.push(Math.abs(sorted[i].pnl));
  }
  if (lossesAfterLoss.length >= 3 && lossesAfterWin.length >= 3) {
    const avgAL = lossesAfterLoss.reduce((s, v) => s + v, 0) / lossesAfterLoss.length;
    const avgAW = lossesAfterWin.reduce((s, v) => s + v, 0) / lossesAfterWin.length;
    if (avgAL > avgAW * 1.35) {
      const excess = (avgAL - avgAW) * lossesAfterLoss.length;
      const pct = Math.round((avgAL / avgAW - 1) * 100);
      leaks.push({
        id: 'size_escalation',
        costDollars: excess,
        occurrences: lossesAfterLoss.length,
        confidence: Math.min(1, lossesAfterLoss.length / 10),
        title_he: 'הגדלת סיכון אחרי הפסד',
        title_en: 'Sizing up after losses',
        detail_he: `הפסדים אחרי הפסד קודם גדולים ב-${pct}% — עודף של ${fmtUsd(excess)} לעומת ההפסד הרגיל שלך.`,
        detail_en: `Losses taken right after a prior loss run ${pct}% larger — ${fmtUsd(excess)} of excess damage vs your normal loss.`,
        fixTemplateId: 'flat_risk',
      });
    }
  }

  // ── No stop after 2 losses: P&L of trades taken after the day's 2nd loss ──
  let afterTwoCount = 0,
    afterTwoPnl = 0;
  for (const day of days.values()) {
    let losses = 0;
    for (const t of day) {
      if (losses >= 2) {
        afterTwoCount++;
        afterTwoPnl += t.pnl;
      }
      if (isL(t)) losses++;
    }
  }
  if (afterTwoCount >= 5 && afterTwoPnl < 0) {
    leaks.push({
      id: 'no_stop_after_losses',
      costDollars: -afterTwoPnl,
      occurrences: afterTwoCount,
      confidence: Math.min(1, afterTwoCount / 15),
      title_he: 'המשך מסחר אחרי 2 הפסדים',
      title_en: 'Trading on after 2 losses',
      detail_he: `עסקאות שנלקחו אחרי ההפסד השני ביום עלו לך ${fmtUsd(afterTwoPnl)}- ב-${afterTwoCount} עסקאות.`,
      detail_en: `Trades taken after the day's 2nd loss cost you ${fmtUsd(afterTwoPnl)} across ${afterTwoCount} trades.`,
      fixTemplateId: 'stop_after_two_losses',
    });
  }

  // ── Weak hour: worst entry hour by total P&L ──
  const byHour = new Map<number, { pnl: number; n: number }>();
  for (const t of trades) {
    const m = timeMins(t.entry_time);
    if (m === null) continue;
    const h = Math.floor(m / 60);
    const cur = byHour.get(h) ?? { pnl: 0, n: 0 };
    cur.pnl += t.pnl;
    cur.n++;
    byHour.set(h, cur);
  }
  let worstHour = -1,
    worstHourPnl = 0,
    worstHourN = 0;
  for (const [h, s] of byHour) {
    if (s.n >= 5 && s.pnl < worstHourPnl) {
      worstHour = h;
      worstHourPnl = s.pnl;
      worstHourN = s.n;
    }
  }
  if (worstHour >= 0 && worstHourPnl < 0) {
    const hh = String(tz ? utcHourToTz(worstHour, tz) : worstHour).padStart(2, '0');
    leaks.push({
      id: 'weak_hour',
      costDollars: -worstHourPnl,
      occurrences: worstHourN,
      confidence: Math.min(1, worstHourN / 15),
      title_he: 'שעה חלשה',
      title_en: 'Weak hour',
      detail_he: `כניסות בשעה ${hh}:00 עלו לך ${fmtUsd(worstHourPnl)}- ב-${worstHourN} עסקאות.`,
      detail_en: `Entries during ${hh}:00 cost you ${fmtUsd(worstHourPnl)} across ${worstHourN} trades.`,
      fixTemplateId: 'avoid_weak_hour',
      fixParam: worstHour,
    });
  }

  // ── Low discipline: checklist score < 0.5 vs ≥ 0.8 ──
  const lowDisc = trades.filter((t) => t.discipline_score != null && t.discipline_score < 0.5);
  const highDisc = trades.filter((t) => t.discipline_score != null && t.discipline_score >= 0.8);
  if (lowDisc.length >= 5 && highDisc.length >= 5) {
    const lowPnl = lowDisc.reduce((s, t) => s + t.pnl, 0);
    const highAvg = highDisc.reduce((s, t) => s + t.pnl, 0) / highDisc.length;
    if (lowPnl < 0) {
      leaks.push({
        id: 'low_discipline',
        costDollars: -lowPnl,
        occurrences: lowDisc.length,
        confidence: Math.min(1, lowDisc.length / 12),
        title_he: "עסקאות בלי צ'קליסט",
        title_en: 'Low-checklist trades',
        detail_he: `עסקאות עם משמעת מתחת ל-50% עלו לך ${fmtUsd(lowPnl)}-. עסקאות ממושמעות מרוויחות בממוצע ${fmtUsd(highAvg)}.`,
        detail_en: `Trades with checklist discipline below 50% cost you ${fmtUsd(lowPnl)}. Your disciplined trades average ${fmtUsd(highAvg)}.`,
        fixTemplateId: 'checklist_every_trade',
      });
    }
  }

  // ── Daily loss breach: damage taken beyond the user's own max-loss line ──
  if (dailyMaxLoss > 0) {
    let breachDays = 0,
      breachExcess = 0;
    for (const day of days.values()) {
      let run = 0,
        excess = 0;
      for (const t of day) {
        const beyond = run <= -dailyMaxLoss;
        run += t.pnl;
        if (beyond) excess += t.pnl;
      }
      if (excess < 0) {
        breachDays++;
        breachExcess += excess;
      }
    }
    if (breachDays >= 2) {
      leaks.push({
        id: 'daily_loss_breach',
        costDollars: -breachExcess,
        occurrences: breachDays,
        confidence: Math.min(1, breachDays / 5),
        title_he: 'חציית מגבלת הפסד יומית',
        title_en: 'Breaking your daily loss limit',
        detail_he: `ב-${breachDays} ימים המשכת לסחור אחרי שחצית את מגבלת ההפסד שלך — עוד ${fmtUsd(breachExcess)}- מעבר לקו.`,
        detail_en: `On ${breachDays} days you kept trading past your own max-loss line — another ${fmtUsd(breachExcess)} beyond it.`,
        fixTemplateId: 'respect_daily_stop',
      });
    }
  }

  return leaks.sort((a, b) => b.costDollars - a.costDollars);
}

// ─── Assignments ─────────────────────────────────────────────

export type TemplateId =
  | 'cooldown_after_loss'
  | 'max_trades_per_day'
  | 'stop_after_two_losses'
  | 'flat_risk'
  | 'avoid_weak_hour'
  | 'checklist_every_trade'
  | 'respect_daily_stop';

export type AssignmentUnit = 'trades' | 'days';
export type AssignmentStatus = 'active' | 'passed' | 'failed' | 'abandoned';

export interface AssignmentTemplate {
  id: TemplateId;
  unit: AssignmentUnit;
  /** Default length: number of trades or trading days. */
  defaultTarget: number;
  /** Compliance % required to pass (0-1). */
  passThreshold: number;
  /** Tunable parameter, meaning depends on template. */
  defaultParam: number;
  title_he: string;
  title_en: string;
  rule_he: (param: number) => string;
  rule_en: (param: number) => string;
  why_he: string;
  why_en: string;
}

export const ASSIGNMENT_TEMPLATES: AssignmentTemplate[] = [
  {
    id: 'cooldown_after_loss',
    unit: 'trades',
    defaultTarget: 20,
    passThreshold: 0.8,
    defaultParam: 30,
    title_he: 'צינון אחרי הפסד',
    title_en: 'Cooldown after a loss',
    rule_he: (p) => `אחרי כל הפסד — לפחות ${p} דקות בלי עסקה חדשה`,
    rule_en: (p) => `After every loss — wait at least ${p} minutes before the next trade`,
    why_he:
      'הדחף "להחזיר את הכסף" הוא הרגש היקר ביותר במסחר. הפסקה קצרה מחזירה אותך מהאמיגדלה לקליפת המוח.',
    why_en:
      'The urge to "make it back" is the most expensive emotion in trading. A short pause moves you from amygdala back to cortex.',
  },
  {
    id: 'max_trades_per_day',
    unit: 'days',
    defaultTarget: 10,
    passThreshold: 0.8,
    defaultParam: 3,
    title_he: 'מקסימום עסקאות ביום',
    title_en: 'Max trades per day',
    rule_he: (p) => `עד ${p} עסקאות ביום מסחר`,
    rule_en: (p) => `No more than ${p} trades per trading day`,
    why_he: 'סלקטיביות היא שריר. כשיש לך רק 3 כדורים, אתה יורה רק על סטאפים מהפלייבוק.',
    why_en: 'Selectivity is a muscle. With only 3 bullets, you only fire at playbook setups.',
  },
  {
    id: 'stop_after_two_losses',
    unit: 'days',
    defaultTarget: 10,
    passThreshold: 0.8,
    defaultParam: 2,
    title_he: 'עצירה אחרי 2 הפסדים',
    title_en: 'Stop after 2 losses',
    rule_he: (p) => `${p} הפסדים ביום = סיום יום המסחר`,
    rule_en: (p) => `${p} losses in a day = done trading for the day`,
    why_he: 'אחרי שני הפסדים אתה כבר לא סוחר את השוק — אתה סוחר את הרגשות שלך. מחר יש שוק חדש.',
    why_en:
      "After two losses you're no longer trading the market — you're trading your emotions. The market reopens tomorrow.",
  },
  {
    id: 'flat_risk',
    unit: 'trades',
    defaultTarget: 20,
    passThreshold: 0.8,
    defaultParam: 1.5,
    title_he: 'סיכון אחיד',
    title_en: 'Flat risk',
    rule_he: (p) => `גודל פוזיציה קבוע — בלי לחרוג מפי ${p} מהגודל החציוני שלך`,
    rule_en: (p) => `Fixed position size — never exceed ${p}× your median size`,
    why_he: 'הגדלת סיכון אחרי הפסד הופכת סדרת הפסדים רגילה לחור בחשבון. R אחיד = שרידות.',
    why_en:
      'Sizing up after a loss turns a normal losing streak into a blown account. Flat R = survival.',
  },
  {
    id: 'avoid_weak_hour',
    unit: 'trades',
    defaultTarget: 20,
    passThreshold: 0.9,
    defaultParam: -1,
    title_he: 'להימנע מהשעה החלשה',
    title_en: 'Avoid your weak hour',
    rule_he: (p) =>
      `בלי כניסות חדשות בשעה ${p >= 0 ? String(p).padStart(2, '0') + ':00' : 'החלשה שלך'}`,
    rule_en: (p) =>
      `No new entries during ${p >= 0 ? String(p).padStart(2, '0') + ':00' : 'your weak hour'}`,
    why_he: 'הנתונים שלך כבר יודעים מתי אתה מפסיד. תן להם לעבוד בשבילך.',
    why_en: 'Your own data already knows when you lose. Let it work for you.',
  },
  {
    id: 'checklist_every_trade',
    unit: 'trades',
    defaultTarget: 20,
    passThreshold: 0.8,
    defaultParam: 0.8,
    title_he: "צ'קליסט בכל עסקה",
    title_en: 'Checklist on every trade',
    rule_he: (p) => `כל עסקה עם צ'קליסט סטאפ מלא (${Math.round(p * 100)}%+ מהכללים)`,
    rule_en: (p) =>
      `Every trade with a completed setup checklist (${Math.round(p * 100)}%+ of rules)`,
    why_he: "ואן ת'ארפ: טעות = הפרת כלל שלך, גם אם הרווחת. הצ'קליסט הופך תהליך למדיד.",
    why_en:
      'Van Tharp: a mistake = breaking your own rule, even on a winner. The checklist makes process measurable.',
  },
  {
    id: 'respect_daily_stop',
    unit: 'days',
    defaultTarget: 10,
    passThreshold: 0.9,
    defaultParam: 0,
    title_he: 'כיבוד מגבלת ההפסד',
    title_en: 'Respect the daily stop',
    rule_he: () => 'הפסד יומי מגיע למגבלה = אפס עסקאות נוספות',
    rule_en: () => 'Daily loss hits your limit = zero further trades',
    why_he: 'המגבלה שקבעת ביום רגוע היא ההחלטה הכי טובה שלך. ביום אדום — רק לבצע אותה.',
    why_en:
      'The limit you set on a calm day is your best decision. On a red day your only job is executing it.',
  },
];

export function getTemplate(id: TemplateId | string): AssignmentTemplate | undefined {
  return ASSIGNMENT_TEMPLATES.find((t) => t.id === id);
}

export interface CoachAssignment {
  id: string;
  user_id?: string;
  template_id: TemplateId;
  started_at: string; // ISO timestamp
  target: number; // trades or days, per template unit
  param: number;
  status: AssignmentStatus;
  completed_at?: string | null;
  /** Final compliance 0-1, frozen on completion. */
  final_compliance?: number | null;
}

export interface AssignmentProgress {
  /** Trades or days elapsed since start (capped at target). */
  elapsed: number;
  target: number;
  compliant: number;
  violations: number;
  /** 0-1 over elapsed units. */
  compliance: number;
  /** Status the assignment *should* have given current data. */
  status: AssignmentStatus;
  unit: AssignmentUnit;
}

/** Trades on/after the assignment start, chronological. */
function tradesSince(trades: Trade[], startedAt: string): Trade[] {
  const startDate = startedAt.slice(0, 10);
  return sortChrono(trades.filter((t) => t.trade_date >= startDate));
}

function median(nums: number[]): number {
  if (!nums.length) return 0;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/**
 * Evaluate assignment compliance against real trades. Per-trade templates mark
 * each trade compliant/violating; per-day templates mark whole days.
 */
export function evalAssignment(
  a: CoachAssignment,
  allTrades: Trade[],
  beRange: number,
  dailyMaxLoss: number = 0,
): AssignmentProgress {
  const tpl = getTemplate(a.template_id);
  const scoped = tradesSince(allTrades, a.started_at);
  const empty: AssignmentProgress = {
    elapsed: 0,
    target: a.target,
    compliant: 0,
    violations: 0,
    compliance: 1,
    status: a.status === 'active' ? 'active' : a.status,
    unit: tpl?.unit ?? 'trades',
  };
  if (!tpl || scoped.length === 0) return empty;

  let flags: boolean[] = []; // true = compliant, one per unit, chronological

  if (tpl.unit === 'trades') {
    switch (a.template_id) {
      case 'cooldown_after_loss': {
        const revenge = new Set(findRevengeTrades(scoped, beRange, a.param).map((t) => t.id));
        flags = scoped.map((t) => !revenge.has(t.id));
        break;
      }
      case 'flat_risk': {
        const med = median(scoped.map((t) => t.size ?? 0).filter((s) => s > 0));
        flags = scoped.map((t) => t.size == null || med === 0 || t.size <= med * a.param);
        break;
      }
      case 'avoid_weak_hour':
        flags = scoped.map((t) => {
          const m = timeMins(t.entry_time);
          return m === null || Math.floor(m / 60) !== a.param;
        });
        break;
      case 'checklist_every_trade':
        flags = scoped.map((t) => t.discipline_score != null && t.discipline_score >= a.param);
        break;
      default:
        flags = scoped.map(() => true);
    }
    flags = flags.slice(0, a.target);
  } else {
    const days = groupByDay(scoped);
    const dayFlags: boolean[] = [];
    for (const day of days.values()) {
      switch (a.template_id) {
        case 'max_trades_per_day':
          dayFlags.push(day.length <= a.param);
          break;
        case 'stop_after_two_losses': {
          let losses = 0,
            violated = false;
          for (const t of day) {
            if (losses >= a.param) {
              violated = true;
              break;
            }
            if (classifyPnl(t.pnl, beRange) === 'loss') losses++;
          }
          dayFlags.push(!violated);
          break;
        }
        case 'respect_daily_stop': {
          if (dailyMaxLoss <= 0) {
            dayFlags.push(true);
            break;
          }
          let run = 0,
            violated = false;
          for (const t of day) {
            if (run <= -dailyMaxLoss) {
              violated = true;
              break;
            }
            run += t.pnl;
          }
          dayFlags.push(!violated);
          break;
        }
        default:
          dayFlags.push(true);
      }
    }
    flags = dayFlags.slice(0, a.target);
  }

  const compliant = flags.filter(Boolean).length;
  const elapsed = flags.length;
  const compliance = elapsed ? compliant / elapsed : 1;
  const done = elapsed >= a.target;
  const status: AssignmentStatus =
    a.status !== 'active'
      ? a.status
      : done
        ? compliance >= tpl.passThreshold
          ? 'passed'
          : 'failed'
        : 'active';

  return {
    elapsed,
    target: a.target,
    compliant,
    violations: elapsed - compliant,
    compliance,
    status,
    unit: tpl.unit,
  };
}

/** Discipline rank from passed-assignment count — the progression ladder. */
export function disciplineRank(passedCount: number): {
  level: number;
  name_he: string;
  name_en: string;
} {
  const ranks = [
    { at: 0, he: 'מתחיל', en: 'Novice' },
    { at: 1, he: 'מתלמד', en: 'Apprentice' },
    { at: 3, he: 'ממושמע', en: 'Disciplined' },
    { at: 6, he: 'מקצוען', en: 'Professional' },
    { at: 10, he: 'מאסטר', en: 'Master' },
  ];
  let level = 0;
  for (let i = 0; i < ranks.length; i++) if (passedCount >= ranks[i].at) level = i;
  return { level, name_he: ranks[level].he, name_en: ranks[level].en };
}
